import { type Client } from 'types/api/attacks/client'
import { type TargetingStrategy } from 'types/api/attacks/details/targeting'
import { type AwsLoadBalancerResponse } from 'types/api/aws/metadata'
import { type UserDefinedServiceJiraDefaults } from 'types/api/jira/user-defined-jira-defaults'
import { type K8sClient } from 'types/api/kubernetes/kubernetes'
import { type LoadGenerator } from 'types/api/services/load-generator'
import { type PagedResponse } from 'types/paged-response'
import {
  type ScheduleSettings,
  type ScheduleTriggerType
} from 'types/schedules/schedules'

export type ServiceIgnoreReason = 'UNIMPORTANT' | 'UNRECOGNIZED' | 'INCORRECT'

export interface ServiceIgnoredInfo {
  reason: ServiceIgnoreReason
  detail?: string
}

export interface ServiceActiveTargets {
  clients: Client[]
  k8sClients: K8sClient[]
}

export interface SchedulableTest {
  reliabilityTestId: string
  dependencyId?: string
  status: ScheduleTriggerType
}

export interface ServiceOwner {
  identifier: string
}

export enum EvaluationState {
  NoDeploymentFound = 'NO_DEPLOYMENT_FOUND',
  Evaluated = 'EVALUATED',
  RisksNotIncluded = 'RISKS_NOT_INCLUDED' // set when a service's test suite excludes all evaluate-able risks
}

export interface RiskSummaryStats {
  atRisk: number
  evaluationState: EvaluationState
  previousIntervalAtRisk: number // -1 means no previous data
  total: number
}

export interface MultiSelectTags {
  [key: string]: string[]
}

export interface ApplicationSelector {
  applicationName: string
}

// TODO: Rename this to Service when we refactor the existing `Service`
type UserDefinedServiceBase = {
  teamId: string
  serviceId: string
  processSignature?: string
  processName?: string
  processId?: string
  name: string
  description?: string | null
  awsTargets: AwsLoadBalancerResponse[]
  eligibleForGremlinAwsStatusCheck: boolean
  enabledGremlinAwsStatusCheck: boolean
  createdFromOnboarding?: boolean
  createdAt: Date | string
  updatedAt: Date | string // the date/time of any change to the service
  statusCheckIds?: string[]
  scheduleSettings?: ScheduleSettings
  schedulableTests: SchedulableTest[]
  autoCreatingJiraTicketsEnabled: boolean
  defaultJiraIssueFields: UserDefinedServiceJiraDefaults
  modifiedDate: string // the date/time of any user-initiated change to the service
  loadGenerators?: LoadGenerator[]
  tags: {
    environment?: string
    [key: string]: string
  }
  serviceOwner?: ServiceOwner
  cachedScore: number
  riskSummaryStats?: RiskSummaryStats
  teamOwningProcess?: string
  multiSelectTags?: MultiSelectTags
}

export type InfraService = UserDefinedServiceBase & {
  applicationSelector?: never
  targetingStrategy: TargetingStrategy
}

export type FailureFlagService = UserDefinedServiceBase & {
  applicationSelector: ApplicationSelector
  targetingStrategy?: never
}

export type UserDefinedService = InfraService | FailureFlagService

export type RunAllTestRequest = {
  tests: string[]
}

export enum ProcessProvenance {
  ProcessData = 'PROCESSDATA',
  Container = 'CONTAINER',
  Kubernetes = 'KUBERNETES'
}

export type PagedUserDefinedServicesResponse = PagedResponse<UserDefinedService>
